import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  HistoryOutlined,
  SwapOutlined,
  CalendarOutlined,
  DownloadOutlined,
  ReloadOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { vpnAPI } from '@/api/endpoints/vpn.js';
import styles from '@/styles/pages/vpn/KeyHistoryPage.module.css';

const KeyHistoryPage = () => {
  const { keyId } = useParams();
  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Загрузка истории трафика
  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vpnAPI.metrics_history_get(keyId, false);
      if (response.success && response.data) {
        setHistory(response.data);
      } else {
        setHistory([]);
        if (response.error) {
          setError(response.error);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      setError('Ошибка загрузки истории трафика');
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keyId) {
      fetchHistory();
    }
  }, [keyId]);

  // Форматирование байт
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Б';
    const k = 1024;
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Форматирование даты
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const logDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (logDate.getTime() === today.getTime()) {
      return 'Сегодня';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (logDate.getTime() === yesterday.getTime()) {
      return 'Вчера';
    }
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Подсчет статистики
  const getStats = () => {
    const totalBytes = history.reduce((sum, log) => sum + (log.bytes_transferred || 0), 0);
    const avgBytes = history.length > 0 ? totalBytes / history.length : 0;
    const maxBytes = history.length > 0 ? Math.max(...history.map(log => log.bytes_transferred || 0)) : 0;
    return { totalBytes, avgBytes, maxBytes, totalRequests: history.length };
  };

  const stats = getStats();

  // Группировка по дням
  const groupByDay = () => {
    const grouped = new Map();
    history.forEach(log => {
      const day = new Date(log.recorded_at).toDateString();
      if (!grouped.has(day)) {
        grouped.set(day, []);
      }
      grouped.get(day).push(log);
    });
    return grouped;
  };

  const groupedHistory = groupByDay();

  // Экспорт в CSV
  const exportToCSV = () => {
    const headers = ['Дата и время', 'Трафик (байты)', 'Трафик (формат)'];
    const rows = history.map(log => [
      log.recorded_at,
      log.bytes_transferred,
      formatBytes(log.bytes_transferred)
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `history_${keyId}_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обновление данных
  const handleRefresh = () => {
    fetchHistory();
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Максимальное значение для графика
  const maxTraffic = Math.max(...history.map(log => log.bytes_transferred || 0), 1);

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowLeftOutlined /> Назад
          </button>
          <div className={styles.headerCenter}>
            <HistoryOutlined className={styles.headerIcon} />
            <div>
              <h1 className={styles.title}>История трафика</h1>
              <div className={styles.keyInfo}>
                <KeyOutlined className={styles.keyIcon} />
                <span className={styles.keyId}>Ключ: {keyId}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.refreshButton} onClick={handleRefresh} title="Обновить">
              <ReloadOutlined /> Обновить
            </button>
            <button className={styles.exportButton} onClick={exportToCSV} title="Экспорт">
              <DownloadOutlined /> CSV
            </button>
          </div>
        </div>

        {/* Ошибка */}
        {error && (
          <div className={styles.errorMessage}>
            <span>⚠️ {error}</span>
          </div>
        )}

        {/* Карточки статистики */}
        {!error && history.length > 0 && (
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <CalendarOutlined />
              </div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Общий трафик</span>
                <span className={styles.statValue}>{formatBytes(stats.totalBytes)}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <SwapOutlined />
              </div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Средний за запись</span>
                <span className={styles.statValue}>{formatBytes(stats.avgBytes)}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <CalendarOutlined />
              </div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Всего записей</span>
                <span className={styles.statValue}>{stats.totalRequests}</span>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <HistoryOutlined />
              </div>
              <div className={styles.statContent}>
                <span className={styles.statLabel}>Максимум за запись</span>
                <span className={styles.statValue}>{formatBytes(stats.maxBytes)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Основной контент */}
        <div className={styles.historyContainer}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Загрузка истории трафика...</p>
            </div>
          ) : history.length === 0 ? (
            <div className={styles.emptyState}>
              <HistoryOutlined className={styles.emptyIcon} />
              <h3>Нет данных</h3>
              <p>История трафика для этого ключа пуста</p>
              <span className={styles.emptyHint}>
                Ключ: {keyId}
              </span>
            </div>
          ) : (
            <>
              {/* Timeline визуализация */}
              <div className={styles.timelineSection}>
                <h2 className={styles.sectionTitle}>Временная шкала трафика</h2>
                <div className={styles.timeline}>
                  {history.slice(-20).map((log, idx) => {
                    const height = (log.bytes_transferred / maxTraffic) * 100;
                    return (
                      <div key={idx} className={styles.timelineBarWrapper}>
                        <div
                          className={styles.timelineBar}
                          style={{ height: `${Math.max(height, 4)}%` }}
                          title={`${formatDateTime(log.recorded_at)}: ${formatBytes(log.bytes_transferred)}`}
                        ></div>
                        <span className={styles.timelineLabel}>
                          {new Date(log.recorded_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Группировка по дням */}
              <div className={styles.historyList}>
                <h2 className={styles.sectionTitle}>Детальная история</h2>
                {Array.from(groupedHistory.entries()).map(([day, logs]) => (
                  <div key={day} className={styles.dayGroup}>
                    <div className={styles.dayHeader}>
                      <CalendarOutlined />
                      <span>{formatDate(logs[0].recorded_at)}</span>
                      <span className={styles.dayTotal}>
                        {formatBytes(logs.reduce((sum, log) => sum + log.bytes_transferred, 0))}
                      </span>
                    </div>
                    <div className={styles.dayLogs}>
                      {logs.map((log, idx) => (
                        <div key={idx} className={styles.logEntry}>
                          <div className={styles.logTime}>
                            {new Date(log.recorded_at).toLocaleTimeString('ru-RU', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </div>
                          <div className={styles.logTraffic}>
                            <div
                              className={styles.logTrafficBar}
                              style={{ width: `${(log.bytes_transferred / maxTraffic) * 100}%` }}
                            ></div>
                            <span className={styles.logTrafficValue}>
                              {formatBytes(log.bytes_transferred)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeyHistoryPage;